import {
  faArrowDown,
  faArrowUp,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, type BadgeProps, type Color } from 'turnip/Badge/Badge';
import type { Maybe } from 'types/Utilities';
import { decimalToPercent } from 'utils/formatNumber';

type StatBadgeProps = {
  /** Decimal number representing the change in value, e.g. 0.05 for 5%. */
  value: Maybe<number>;
} & Pick<BadgeProps, 'size'>;

/**
 * Display a badge for an increase or decrease in a stat.
 */
export function StatBadge({ value, size }: StatBadgeProps) {
  const changeVerb = getChangeVerb(value);
  const { color, icon } = changeDesign[changeVerb];

  return (
    <Badge
      color={color}
      icon={icon && <FontAwesomeIcon icon={icon} className="fa-width-auto" />}
      size={size}
    >
      <span className="font-bold">
        {typeof value === 'number' ? (
          <>
            <span className="sr-only">{changeVerb}</span>
            {decimalToPercent(Math.abs(value), 'max-two')}
          </>
        ) : (
          <span aria-hidden>—</span>
        )}
      </span>
    </Badge>
  );
}

function getChangeVerb(value: Maybe<number>): ChangeVerb {
  if (typeof value !== 'number' || value === 0) return 'changed';
  else if (value > 0) return 'increased';
  else return 'decreased';
}

type ChangeVerb = 'increased' | 'decreased' | 'changed';
const changeDesign: Record<
  ChangeVerb,
  { color: Color; icon?: IconDefinition }
> = /*tw*/ {
  increased: {
    color: 'green',
    icon: faArrowUp,
  },
  decreased: {
    color: 'red',
    icon: faArrowDown,
  },
  changed: {
    color: 'gray',
  },
};
